import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Spinner, Subheading } from '@treams/treams-ui';

import { SelectConversationType } from './select-conversation-type';

type EvaluationForm = {
	id: string;
	title: graphql.LocalizedString;
	description?: graphql.LocalizedAttributedString | null;
	jobTitleIDs?: string[] | null;
	useOnlyInCycle?: boolean | null;
};

type ConversationTemplateSelectProps = {
	titlePrefix?: string;
	userID: string;
	value?: string;
	onChange: (evaluationFormID: string | undefined) => void;
};

function containsJobTitle(evaluationForm: EvaluationForm, jobTitleIDs: string[]) {
	if (!evaluationForm.jobTitleIDs || evaluationForm.jobTitleIDs.length === 0) {
		return true;
	}

	for (const evaluationFormJobTitleID of evaluationForm.jobTitleIDs) {
		if (jobTitleIDs.includes(evaluationFormJobTitleID)) {
			return true;
		}
	}

	return false;
}

function useConversationTemplates(userID: string): {
	templates: EvaluationForm[];
	loading: boolean;
} {
	const { isWorkspaceSuperuser } = useViewerRoles();

	const evaluationFormsQuery = graphql.useEvaluationFormsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});
	const forms = evaluationFormsQuery.data?.evaluationForms.edges.map((edge) => edge.node);

	const jobPositionsQuery = graphql.useJobPositionsQuery({
		variables: { id: userID, status: graphql.JobPositionStatus.Active },
	});

	const userJobTitleIDs = jobPositionsQuery.data?.user.jobPositions.map(
		(jobPosition) => jobPosition.jobTitle.id,
	);

	return {
		templates:
			forms?.filter(
				(form) =>
					isWorkspaceSuperuser ||
					(!form.useOnlyInCycle && containsJobTitle(form, userJobTitleIDs ?? [])),
			) ?? [],
		loading: jobPositionsQuery.loading || evaluationFormsQuery.loading,
	};
}

export function ConversationTemplateSelect(props: ConversationTemplateSelectProps) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { loading, templates } = useConversationTemplates(props.userID);

	const forms = templates
		.slice()
		.sort((a, b) => localize(a.title).text.localeCompare(localize(b.title).text));

	return (
		<>
			<Subheading mb={2}>
				{props.titlePrefix ? `${props.titlePrefix} ` : ''}
				{t('evaluations.create.modal.formLabel')}
			</Subheading>

			{loading ? (
				<Box display="flex" justifyContent="center" mb={3}>
					<Spinner />
				</Box>
			) : (
				<SelectConversationType
					forms={forms as graphql.EvaluationFormFragment[]}
					value={props.value}
					onChange={props.onChange}
				/>
			)}
		</>
	);
}
